import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useAuth } from './auth';
import { IFCWithChildren } from '../interfaces/IFCWithChildren';
import { useCookies } from '../hooks/use-cookies';
import { CurrencyIDTypesEnum } from '../enums/CurrencyTypesEnum';
import { cookies } from '../constants/cookies';

export type TPortfolioCalculationMethods = 'default' | 'sumTotal';

interface IAccountPreferencesContextData {
  currency: CurrencyIDTypesEnum;
  portfolioMethod: TPortfolioCalculationMethods;
  handleChangeCurrency: (value: CurrencyIDTypesEnum) => void;
  handleChangePortfolioCalculationMethod: (
    value: TPortfolioCalculationMethods
  ) => void;
}

const AccountPreferencesContext = createContext(
  {} as IAccountPreferencesContextData
);

const AccountPreferencesProvider: IFCWithChildren = ({ children }) => {
  const { getItem, setItem } = useCookies();
  const { updateUserParam, userParams } = useAuth();
  const [portfolioMethod, setPortfolioMethod] =
    useState<TPortfolioCalculationMethods>(
      (getItem(cookies.PORTFOLIO_METHOD) as TPortfolioCalculationMethods) ||
        userParams.portifolioMethod ||
        'default'
    );

  const [currency, setCurrency] = useState<CurrencyIDTypesEnum>(
    getItem(cookies.PORTFOLIO_CURRENCY)
      ? (Number(getItem(cookies.PORTFOLIO_CURRENCY)) as CurrencyIDTypesEnum)
      : userParams.currencyId || CurrencyIDTypesEnum.US
  );

  const handleChangeCurrency = useCallback(
    (value: CurrencyIDTypesEnum) => {
      setCurrency(value);
      setItem(cookies.PORTFOLIO_CURRENCY, String(value));
      updateUserParam('currencyId', value);
    },
    [setItem, updateUserParam]
  );

  const handleChangePortfolioCalculationMethod = useCallback(
    (value: TPortfolioCalculationMethods) => {
      setPortfolioMethod(value);
      updateUserParam('portifolioMethod', value);
    },
    [updateUserParam]
  );

  useEffect(() => {
    setPortfolioMethod(userParams.portifolioMethod);
    setCurrency(userParams.currencyId);
  }, [userParams]);

  return (
    <AccountPreferencesContext.Provider
      value={{
        currency,
        portfolioMethod,
        handleChangePortfolioCalculationMethod,
        handleChangeCurrency,
      }}
    >
      {children}
    </AccountPreferencesContext.Provider>
  );
};

const useAccountPreferences = (): IAccountPreferencesContextData => {
  return useContext(AccountPreferencesContext);
};

export { AccountPreferencesProvider, useAccountPreferences };
